/*
 * Zustand Utilities
 *
 * This file contains utility functions for working with Zustand stores.
 */

/*
 * ==========================================
 * COMPUTED
 * ==========================================
 */

type EqualityFn<T> = (a: T, b: T) => boolean
type EqualityFns<TDeps extends readonly unknown[]> = {
  [Item in keyof TDeps]?: EqualityFn<TDeps[Item]>
}

export function computed<const TDeps extends readonly unknown[] = unknown[], TResult = unknown>(
  depsFn: () => TDeps,
  computeFn: (deps: TDeps, ...args: any[]) => TResult,
  equalityFns: EqualityFn<TDeps[number]> | EqualityFns<TDeps> = Object.is
): () => TResult {
  let cachedDeps: TDeps | undefined
  let cachedResult: TResult
  return (...args: any[]) => {
    const deps = depsFn()
    if (cachedDeps === undefined || !equalDependencies(cachedDeps, deps, equalityFns)) {
      cachedDeps = deps
      cachedResult = computeFn(deps, ...args)
    }
    return cachedResult
  }
}

/*
 * ==========================================
 * MISC UTILITIES
 * ==========================================
 */

function equalDependencies<TDeps extends readonly unknown[] = readonly unknown[]>(
  deps1: TDeps,
  deps2: TDeps,
  equalityFns: EqualityFn<TDeps[number]> | EqualityFns<TDeps>
): boolean {
  return (
    deps1.length === deps2.length &&
    deps1.every((dep, i) => {
      const equalityFn = typeof equalityFns === "function" ? equalityFns : (equalityFns[i] ?? Object.is)
      return equalityFn(dep, deps2[i])
    })
  )
}
